import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {useTranslation} from 'react-i18next';
import {Location} from 'history';
import useHistoryState from 'use-history-state';
import {callAlertWeb, callNativeWeb, footerShowEventWeb} from 'common/appInterface';
import client from "lib/CommonApi/client";
import {AxiosError} from "axios";

const TermsListPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL03";
  const screenName = t("APPL03.title");

  const [cardApply] = useState<Partial<CardApplyStep2>>(location.state.apply);

  const [debitState, setDebitState] = useState<CardDebitState | null>(location.state.userInfo);

  /** 전체동의 체크배열 */
  const [allItems, setAllItems] = useState<Array<string>>(["cardProductGatradeYn", "uniqDiscriminationNoCollectYn", "personalInfoThirdPartyOfferYn", "uniqDiscriminationNoOfferYn", "iniquityUsePreventionOfferYn", "smsCreditCardAdviceYn", "emailCreditCardAdviceYn", "telCreditCardAdviceYn", "writtenCreditCardAdviceYn", "cardMonthlyFeeAutomaticWithdrawalYn"]);
  /** 필수동의 체크배열 */
  const reqItems = useMemo(() => ["cardProductGatradeYn", "uniqDiscriminationNoCollectYn", "personalInfoThirdPartyOfferYn", "uniqDiscriminationNoOfferYn"], []);
  /** 권유동의 체크배열 */
  const adviceItems = useMemo(() => ["smsCreditCardAdviceYn", "emailCreditCardAdviceYn", "telCreditCardAdviceYn", "writtenCreditCardAdviceYn"], []);
  /** 선택 체크배열 */
  const [checkItems, setCheckItems] = useHistoryState<string[]>([], "termsCheck");

  /** 개별동의처리 */
  const changeSingleCheck = useCallback((checked:boolean, value:string) => {
    if (checked) {
      setCheckItems([...checkItems, value], true);
    }
    else {
      setCheckItems(checkItems.filter((el) => el !== value), true);
    }
  }, [checkItems, setCheckItems]);

  /** 전체동의처리 */
  const changeAllCheck = useCallback((checked:boolean) => {
    if (checked) {
      setCheckItems(allItems, true);
    }
    else {
      setCheckItems([], true);
    }
  }, [allItems, setCheckItems]);

  /** 권유동의처리 */
  const changeAdviceCheck = useCallback((checked:boolean) => {
    if (checked) {
      setCheckItems(Array.from(new Set([...checkItems, ...adviceItems])), true);
    }
    else {
      setCheckItems(checkItems.filter((el) => !adviceItems.includes(el)), true);
    }
  }, [setCheckItems, checkItems, adviceItems]);

  /** 약관상세보기 */
  const clickDetail = useCallback((target:string) => {
    navigate("/apply/step1/" + target, { state: location.state });
  }, [navigate, location]);

  /** backKeyEvent_App 처리 (APP > WEB) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** Next 클릭 */
  const clickNext = useCallback(() => {
    navigate("/apply/step2", { state: { ...location.state, apply: { ...cardApply, ...Object.assign({}, ...allItems.map((value) => ({ [value]: "N" })), ...checkItems.map((value) => ({ [value]: "Y" }))) }}});
  }, [navigate, location, allItems, checkItems, cardApply]);

  /** 출금동의여부조회 */
  const callDebitState = useCallback(async () => {
    await client.get('/api/app/manage/debit/state', {
      params: {
        ...location.state.query
      }
    }).then(response => {
      setDebitState(response.data.response);
    }).catch((e: AxiosError) => {
      callAlertWeb('ERROR', '99', e.message);
    });
  }, [location.state.query]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);

    callDebitState();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  useEffect(() => {
    setAllItems(debitState?.monthlyFeeAutoDebitYn === 'Y' ?
        ["cardProductGatradeYn", "uniqDiscriminationNoCollectYn", "personalInfoThirdPartyOfferYn", "uniqDiscriminationNoOfferYn", "iniquityUsePreventionOfferYn", "smsCreditCardAdviceYn", "emailCreditCardAdviceYn", "telCreditCardAdviceYn", "writtenCreditCardAdviceYn"]
        : ["cardProductGatradeYn", "uniqDiscriminationNoCollectYn", "personalInfoThirdPartyOfferYn", "uniqDiscriminationNoOfferYn", "iniquityUsePreventionOfferYn", "smsCreditCardAdviceYn", "emailCreditCardAdviceYn", "telCreditCardAdviceYn", "writtenCreditCardAdviceYn", "cardMonthlyFeeAutomaticWithdrawalYn"]
    );
  }, [debitState]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents pb64-mb">
          <div className="singUpWrapSub">
            <div className="agreeWrap">
              <div className={checkItems.filter((el) => allItems.includes(el)).length === allItems.length ? "check_all check_on" : "check_all"} onClick={(e) => { e.stopPropagation(); changeAllCheck(checkItems.length !== allItems.length);}}>
                <div className="checkboxInner">
                  <input type="checkbox" id="allAgree" checked={checkItems.filter((el) => allItems.includes(el)).length === allItems.length} onChange={(e) => changeAllCheck(e.target.checked)} />
                  <label>
                    <span>{t("APPL03.agreeAll")}</span>
                  </label>
                </div>
              </div>
              <div className="agreeBox">
                <div className="checkbox">
                  <div className="checkboxInner">
                    <input type="checkbox" id="agree01" name="agree01" value="cardProductGatradeYn" checked={checkItems.includes("cardProductGatradeYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                    <label htmlFor="agree01">
                      <span>{t("APPL03.agree01")}</span>
                    </label>
                    <div className="btnMore" onClick={() => clickDetail("cardProductGatradeYn")}><button type="button">보기</button></div>
                  </div>
                </div>
                <div className="checkbox">
                  <div className="checkboxInner">
                    <input type="checkbox" id="agree02" name="agree02" value="uniqDiscriminationNoCollectYn" checked={checkItems.includes("uniqDiscriminationNoCollectYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                    <label htmlFor="agree02">
                      <span>{t("APPL03.agree02")}</span>
                    </label>
                    <div className="btnMore" onClick={() => clickDetail("uniqDiscriminationNoCollectYn")}>
                      <button type="button"><span>보기</span></button>
                    </div>
                  </div>
                </div>
                <div className="checkbox">
                  <div className="checkboxInner">
                    <input type="checkbox" id="agree07" name="agree07" value="personalInfoThirdPartyOfferYn" checked={checkItems.includes("personalInfoThirdPartyOfferYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                    <label htmlFor="agree07">
                      <span>{t("APPL03.agree07")}</span>
                    </label>
                    <div className="btnMore" onClick={() => clickDetail("personalInfoThirdPartyOfferYn")}>
                      <button type="button"><span>보기</span></button>
                    </div>
                  </div>
                </div>
                <div className="checkbox">
                  <div className="checkboxInner">
                    <input type="checkbox" id="agree03" name="agree03" value="uniqDiscriminationNoOfferYn" checked={checkItems.includes("uniqDiscriminationNoOfferYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                    <label htmlFor="agree03">
                      <span>{t("APPL03.agree03")}</span>
                    </label>
                    <div className="btnMore" onClick={() => clickDetail("uniqDiscriminationNoOfferYn")}><button type="button"><span>보기</span></button></div>
                  </div>
                </div>
                <div className="checkbox">
                  <div className="checkboxInner">
                    <input type="checkbox" id="agree04" name="agree04" value="iniquityUsePreventionOfferYn" checked={checkItems.includes("iniquityUsePreventionOfferYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                    <label htmlFor="agree04">
                      <span>{t("APPL03.agree04")}</span>
                    </label>
                    <div className="btnMore" onClick={() => clickDetail("iniquityUsePreventionOfferYn")}><button type="button"><span>보기</span></button></div>
                  </div>
                </div>
                <div className="checkbox mgb10">
                  <div className="checkboxInner">
                    <input type="checkbox" id="agree05" name="agree05" value="agree05" checked={checkItems.filter((el) => adviceItems.includes(el)).length === adviceItems.length} onChange={(e) => changeAdviceCheck(e.target.checked)} />
                    <label htmlFor="agree05">
                      <span>{t("APPL03.agree05")}</span>
                    </label>
                    <div className="btnMore" onClick={() => clickDetail("adviceAgree")}><button type="button"><span>보기</span></button></div>
                  </div>
                </div>
                <div className="checkWrap" style={{ display: "none" }}>
                  <ul className="checkList">
                    <li className="option">
                      <input type="checkbox" id="opt01" name="opt01" value="smsCreditCardAdviceYn" checked={checkItems.includes("smsCreditCardAdviceYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                      <label htmlFor="opt01">
                        <span>문자</span>
                      </label>
                    </li>
                    <li className="option">
                      <input type="checkbox" id="opt02" name="opt02" value="emailCreditCardAdviceYn" checked={checkItems.includes("emailCreditCardAdviceYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                      <label htmlFor="opt02">
                        <span>이메일</span>
                      </label>
                    </li>
                    <li className="option">
                      <input type="checkbox" id="opt03" name="opt03" value="telCreditCardAdviceYn" checked={checkItems.includes("telCreditCardAdviceYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                      <label htmlFor="opt03">
                        <span>전화</span>
                      </label>
                    </li>
                    <li className="option">
                      <input type="checkbox" id="opt04" name="opt04" value="writtenCreditCardAdviceYn" checked={checkItems.includes("writtenCreditCardAdviceYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                      <label htmlFor="opt04">
                        <span>우편</span>
                      </label>
                    </li>
                  </ul>
                </div>
                {debitState?.monthlyFeeAutoDebitYn === 'Y' || (
                    <div className="checkbox mgt45">
                      <div className="checkboxInner">
                        <input type="checkbox" id="agree06" name="agree06" value="cardMonthlyFeeAutomaticWithdrawalYn" checked={checkItems.includes("cardMonthlyFeeAutomaticWithdrawalYn")} onChange={(e) => changeSingleCheck(e.target.checked, e.target.value)} />
                        <label htmlFor="agree06">
                          <span>{t("APPL03.agree06")}</span>
                        </label>
                        <div className="btnMore" onClick={() => clickDetail("cardMonthlyFeeAutomaticWithdrawalYn")}><button type="button"><span>보기</span></button></div>
                      </div>
                    </div>
                )}
              </div>
            </div>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01" disabled={checkItems.filter((el) => reqItems.includes(el)).length !== reqItems.length} onClick={clickNext}><span>{t("COMMON.button.next")}</span></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsListPage;